import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Bot, 
  Shield, 
  Database, 
  ArrowLeft,
  Loader2,
  Search,
  Briefcase,
  MessageSquare,
  FileText,
  Check,
  Globe,
  Lock,
  Palette,
  Upload,
  X
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

// Avatares pré-definidos
const PREDEFINED_AVATARS = [
  { code: 'emoji:robot', emoji: '🤖', label: 'Robô' },
  { code: 'emoji:scales', emoji: '⚖️', label: 'Balança' },
  { code: 'emoji:books', emoji: '📚', label: 'Livros' },
  { code: 'emoji:gavel', emoji: '🔨', label: 'Martelo' },
  { code: 'emoji:shield', emoji: '🛡️', label: 'Escudo' },
  { code: 'emoji:owl', emoji: '🦉', label: 'Coruja' },
  { code: 'emoji:bulb', emoji: '💡', label: 'Ideia' },
  { code: 'emoji:brain', emoji: '🧠', label: 'Cérebro' },
  { code: 'emoji:star', emoji: '⭐', label: 'Estrela' },
  { code: 'emoji:sparkles', emoji: '✨', label: 'Brilho' },
];

export const getEmojiFromCode = (code: string): string => {
  if (!code?.startsWith('emoji:')) return '🤖';
  const avatar = PREDEFINED_AVATARS.find(a => a.code === code);
  return avatar?.emoji || '🤖';
};

interface AssistantSettings {
  id?: string;
  enabled: boolean;
  external_knowledge_allowed: boolean;
  scope_mode: 'ALL' | 'SELECTED';
  assistant_name: string;
  assistant_avatar: string | null;
  response_tone: 'professional' | 'friendly' | 'formal' | 'casual';
}

const RESPONSE_TONES = [
  { 
    value: 'professional' as const, 
    label: 'Profissional', 
    emoji: '💼',
    description: 'Objetivo e direto, adequado para ambiente de trabalho' 
  },
  { 
    value: 'friendly' as const, 
    label: 'Amigável', 
    emoji: '😊',
    description: 'Acolhedor e prestativo, como um colega de trabalho' 
  },
  { 
    value: 'formal' as const, 
    label: 'Formal', 
    emoji: '📜',
    description: 'Extremamente formal, linguagem técnica jurídica' 
  },
  { 
    value: 'casual' as const, 
    label: 'Informal', 
    emoji: '💬',
    description: 'Descontraído e casual, como um amigo próximo' 
  },
];

interface ScopeItem {
  id: string;
  resource_type: string;
  resource_id: string;
}

interface SelectableResource {
  id: string;
  title: string;
  type: 'CASO' | 'LEGAL_CHAT';
  description?: string;
}

export default function AssessorSettingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState<AssistantSettings>({
    enabled: false,
    external_knowledge_allowed: false,
    scope_mode: 'ALL',
    assistant_name: 'Assessor',
    assistant_avatar: null,
    response_tone: 'professional'
  });
  const [scopeItems, setScopeItems] = useState<ScopeItem[]>([]);
  const [resources, setResources] = useState<SelectableResource[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Load settings
      const { data: settingsData, error: settingsError } = await supabase
        .from('assistant_settings')
        .select('*')
        .eq('user_id', user?.id)
        .maybeSingle();

      if (settingsError && settingsError.code !== 'PGRST116') {
        throw settingsError;
      }

      if (settingsData) {
        setSettings({
          id: settingsData.id,
          enabled: settingsData.enabled,
          external_knowledge_allowed: settingsData.external_knowledge_allowed,
          scope_mode: settingsData.scope_mode as 'ALL' | 'SELECTED',
          assistant_name: settingsData.assistant_name || 'Assessor',
          assistant_avatar: settingsData.assistant_avatar || null,
          response_tone: (settingsData as any).response_tone || 'professional'
        });
      }

      // Load scope items
      const { data: scopeData } = await supabase
        .from('assistant_scope_items')
        .select('*')
        .eq('user_id', user?.id);

      setScopeItems(scopeData || []);

      // Load available resources (casos and legal chats)
      const [casosRes, chatsRes] = await Promise.all([
        supabase
          .from('casos')
          .select('id, title, description')
          .eq('user_id', user?.id)
          .order('updated_at', { ascending: false }),
        supabase
          .from('legal_conversations')
          .select('id, title')
          .eq('user_id', user?.id)
          .is('deleted_at', null)
          .order('updated_at', { ascending: false })
      ]);

      const allResources: SelectableResource[] = [
        ...(casosRes.data || []).map(c => ({
          id: c.id,
          title: c.title,
          type: 'CASO' as const,
          description: c.description || undefined
        })),
        ...(chatsRes.data || []).map(c => ({
          id: c.id,
          title: c.title || 'Chat sem título',
          type: 'LEGAL_CHAT' as const
        }))
      ];

      setResources(allResources);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar configurações');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!user) return;
    setIsSaving(true);

    try {
      if (settings.id) {
        // Update existing
        const { error } = await supabase
          .from('assistant_settings')
          .update({
            enabled: settings.enabled,
            external_knowledge_allowed: settings.external_knowledge_allowed,
            scope_mode: settings.scope_mode,
            assistant_name: settings.assistant_name,
            assistant_avatar: settings.assistant_avatar,
            response_tone: settings.response_tone,
            updated_at: new Date().toISOString()
          })
          .eq('id', settings.id);

        if (error) throw error;
      } else {
        // Create new
        const { data, error } = await supabase
          .from('assistant_settings')
          .insert({
            user_id: user.id,
            enabled: settings.enabled,
            external_knowledge_allowed: settings.external_knowledge_allowed,
            scope_mode: settings.scope_mode,
            assistant_name: settings.assistant_name,
            assistant_avatar: settings.assistant_avatar,
            response_tone: settings.response_tone
          })
          .select()
          .single();

        if (error) throw error;
        setSettings(prev => ({ ...prev, id: data.id }));
      }

      toast.success('Configurações salvas!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Erro ao salvar configurações');
    } finally {
      setIsSaving(false);
    }
  };

  const isResourceSelected = (resourceId: string, resourceType: string) => {
    return scopeItems.some(
      item => item.resource_id === resourceId && item.resource_type === resourceType
    );
  };

  const handleToggleResource = async (resource: SelectableResource) => {
    if (!user) return;

    const isSelected = isResourceSelected(resource.id, resource.type);

    try {
      if (isSelected) {
        // Remove
        await supabase
          .from('assistant_scope_items')
          .delete()
          .eq('user_id', user.id)
          .eq('resource_id', resource.id)
          .eq('resource_type', resource.type);

        setScopeItems(prev => 
          prev.filter(item => !(item.resource_id === resource.id && item.resource_type === resource.type))
        );
      } else {
        // Add
        const { data, error } = await supabase
          .from('assistant_scope_items')
          .insert({
            user_id: user.id,
            resource_id: resource.id,
            resource_type: resource.type
          })
          .select()
          .single();

        if (error) throw error;
        setScopeItems(prev => [...prev, data]);
      }
    } catch (error) {
      console.error('Error toggling resource:', error);
      toast.error('Erro ao atualizar seleção');
    }
  };

  const filteredResources = resources.filter(r =>
    r.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const casosCount = resources.filter(r => r.type === 'CASO').length;
  const chatsCount = resources.filter(r => r.type === 'LEGAL_CHAT').length;
  const selectedCount = scopeItems.length;

  if (isLoading) {
    return (
      <SidebarProvider>
        <div className="min-h-screen flex w-full bg-background">
          <AppSidebar />
          <main className="flex-1 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </main>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar />
        <main className="flex-1 overflow-auto">
          <div className="container max-w-4xl py-8 px-4">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Bot className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold">Configurações do Assessor</h1>
                  <p className="text-muted-foreground">
                    Configure como o Assessor acessa seus dados
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              {/* Main toggle */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Ativar Assessor
                  </CardTitle>
                  <CardDescription>
                    Habilite o assistente que conhece todos os seus dados
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="enabled">Ícone flutuante do Assessor</Label>
                      <p className="text-sm text-muted-foreground">
                        {settings.enabled 
                          ? "O ícone flutuante está visível em todas as páginas" 
                          : "O ícone flutuante está oculto"}
                      </p>
                    </div>
                    <Switch
                      id="enabled"
                      checked={settings.enabled}
                      onCheckedChange={(checked) => 
                        setSettings(prev => ({ ...prev, enabled: checked }))
                      }
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Personalization */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Palette className="h-5 w-5" />
                    Personalização
                  </CardTitle>
                  <CardDescription>
                    Personalize a aparência do seu assistente
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Avatar Section */}
                  <div className="space-y-4">
                    <Label>Avatar do Assessor</Label>
                    
                    {/* Preview atual */}
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden border-2 border-primary/30">
                        {settings.assistant_avatar?.startsWith('emoji:') ? (
                          <span className="text-3xl">{getEmojiFromCode(settings.assistant_avatar)}</span>
                        ) : settings.assistant_avatar ? (
                          <img 
                            src={settings.assistant_avatar} 
                            alt="Avatar" 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Bot className="h-8 w-8 text-muted-foreground" />
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {settings.assistant_avatar?.startsWith('emoji:') 
                          ? 'Avatar pré-definido selecionado'
                          : settings.assistant_avatar 
                            ? 'Imagem personalizada'
                            : 'Nenhum avatar selecionado'}
                      </div>
                    </div>

                    {/* Avatares pré-definidos */}
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Escolha um avatar:</Label>
                      <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
                        {PREDEFINED_AVATARS.map((avatar) => (
                          <button
                            key={avatar.code}
                            type="button"
                            onClick={() => setSettings(prev => ({ ...prev, assistant_avatar: avatar.code }))}
                            className={cn(
                              "w-10 h-10 rounded-lg flex items-center justify-center text-xl transition-all border-2 relative",
                              "hover:scale-110 hover:bg-accent",
                              settings.assistant_avatar === avatar.code
                                ? "border-primary bg-primary/10 ring-2 ring-primary/20"
                                : "border-transparent bg-muted"
                            )}
                            title={avatar.label}
                          >
                            {avatar.emoji}
                            {settings.assistant_avatar === avatar.code && (
                              <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                                <Check className="h-2.5 w-2.5 text-primary-foreground" />
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Divisor */}
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-px bg-border" />
                      <span className="text-xs text-muted-foreground">ou</span>
                      <div className="flex-1 h-px bg-border" />
                    </div>
                    
                    {/* Upload personalizado */}
                    <div className="flex items-center gap-3">
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                setSettings(prev => ({
                                  ...prev,
                                  assistant_avatar: reader.result as string
                                }));
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                        />
                        <div className="flex items-center gap-2 px-3 py-2 bg-secondary hover:bg-secondary/80 rounded-md text-sm transition-colors">
                          <Upload className="h-4 w-4" />
                          Enviar imagem personalizada
                        </div>
                      </label>
                      
                      {settings.assistant_avatar && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSettings(prev => ({ ...prev, assistant_avatar: null }))}
                          className="text-destructive hover:text-destructive"
                        >
                          <X className="h-4 w-4 mr-1" />
                          Remover
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Name Section */}
                  <div className="space-y-2">
                    <Label htmlFor="assistant_name">Nome do Assessor</Label>
                    <Input
                      id="assistant_name"
                      value={settings.assistant_name}
                      onChange={(e) => 
                        setSettings(prev => ({ ...prev, assistant_name: e.target.value.slice(0, 50) }))
                      }
                      placeholder="Ex: Meu Assessor Jurídico"
                      maxLength={50}
                    />
                    <p className="text-xs text-muted-foreground">
                      {settings.assistant_name.length}/50 caracteres
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Response Tone */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    Tom de Resposta
                  </CardTitle>
                  <CardDescription>
                    Como o assessor deve se comunicar com você
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <RadioGroup
                    value={settings.response_tone}
                    onValueChange={(value: 'professional' | 'friendly' | 'formal' | 'casual') => 
                      setSettings(prev => ({ ...prev, response_tone: value }))
                    }
                    className="space-y-3"
                  >
                    {RESPONSE_TONES.map(tone => (
                      <div 
                        key={tone.value}
                        className={cn(
                          "flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all",
                          settings.response_tone === tone.value 
                            ? "border-primary bg-primary/5" 
                            : "border-transparent hover:bg-muted"
                        )}
                        onClick={() => setSettings(prev => ({ ...prev, response_tone: tone.value }))}
                      >
                        <RadioGroupItem value={tone.value} id={tone.value} className="mt-1" />
                        <div className="flex-1">
                          <label htmlFor={tone.value} className="flex items-center gap-2 font-medium cursor-pointer">
                            <span className="text-lg">{tone.emoji}</span>
                            {tone.label}
                          </label>
                          <p className="text-sm text-muted-foreground mt-0.5">
                            {tone.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </RadioGroup>
                </CardContent>
              </Card>

              {/* External knowledge toggle */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {settings.external_knowledge_allowed ? (
                      <Globe className="h-5 w-5" />
                    ) : (
                      <Lock className="h-5 w-5" />
                    )}
                    Conhecimento Externo
                  </CardTitle>
                  <CardDescription>
                    Permite que o assessor use conhecimento jurídico geral além dos seus dados
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="external">Permitir conhecimento externo</Label>
                      <p className="text-sm text-muted-foreground">
                        {settings.external_knowledge_allowed 
                          ? "O Assessor pode complementar com conhecimento jurídico geral" 
                          : "O Assessor responde apenas com seus dados internos"}
                      </p>
                    </div>
                    <Switch
                      id="external"
                      checked={settings.external_knowledge_allowed}
                      onCheckedChange={(checked) => 
                        setSettings(prev => ({ ...prev, external_knowledge_allowed: checked }))
                      }
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Scope selection */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Database className="h-5 w-5" />
                    Escopo de Acesso
                  </CardTitle>
                  <CardDescription>
                    Defina quais dados o Assessor pode consultar
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <RadioGroup
                    value={settings.scope_mode}
                    onValueChange={(value) => 
                      setSettings(prev => ({ ...prev, scope_mode: value as 'ALL' | 'SELECTED' }))
                    }
                    className="space-y-3"
                  >
                    <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-accent/50 cursor-pointer">
                      <RadioGroupItem value="ALL" id="all" />
                      <Label htmlFor="all" className="flex-1 cursor-pointer">
                        <div className="font-medium">Acessar tudo da minha conta</div>
                        <div className="text-sm text-muted-foreground">
                          {casosCount} casos e {chatsCount} conversas disponíveis
                        </div>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-accent/50 cursor-pointer">
                      <RadioGroupItem value="SELECTED" id="selected" />
                      <Label htmlFor="selected" className="flex-1 cursor-pointer">
                        <div className="font-medium">Selecionar casos e conversas específicas</div>
                        <div className="text-sm text-muted-foreground">
                          {selectedCount} item(s) selecionado(s)
                        </div>
                      </Label>
                    </div>
                  </RadioGroup>

                  {/* Resource selection */}
                  {settings.scope_mode === 'SELECTED' && (
                    <div className="space-y-4 pt-4 border-t">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Buscar casos e conversas..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-10"
                        />
                      </div>

                      <ScrollArea className="h-[300px] rounded-lg border">
                        <div className="p-2 space-y-1">
                          {filteredResources.length === 0 ? (
                            <p className="text-center text-muted-foreground py-8">
                              Nenhum recurso encontrado
                            </p>
                          ) : (
                            filteredResources.map(resource => {
                              const isSelected = isResourceSelected(resource.id, resource.type);
                              return (
                                <button
                                  key={`${resource.type}-${resource.id}`}
                                  onClick={() => handleToggleResource(resource)}
                                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-accent/50 transition-colors text-left"
                                >
                                  <Checkbox checked={isSelected} />
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      {resource.type === 'CASO' ? (
                                        <Briefcase className="h-4 w-4 text-muted-foreground shrink-0" />
                                      ) : (
                                        <MessageSquare className="h-4 w-4 text-muted-foreground shrink-0" />
                                      )}
                                      <span className="font-medium truncate">{resource.title}</span>
                                      <Badge variant="outline" className="shrink-0">
                                        {resource.type === 'CASO' ? 'Caso' : 'Chat'}
                                      </Badge>
                                    </div>
                                    {resource.description && (
                                      <p className="text-sm text-muted-foreground truncate mt-0.5">
                                        {resource.description}
                                      </p>
                                    )}
                                  </div>
                                  {isSelected && (
                                    <Check className="h-4 w-4 text-primary shrink-0" />
                                  )}
                                </button>
                              );
                            })
                          )}
                        </div>
                      </ScrollArea>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Save button */}
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => navigate('/assessor')}>
                  Ir para o Assessor
                </Button>
                <Button onClick={handleSaveSettings} disabled={isSaving}>
                  {isSaving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Check className="mr-2 h-4 w-4" />
                      Salvar configurações
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}